import React from "react";
import PropTypes from "prop-types";
import TextInfo, {
  TEXT_COLOR,
  TEXT_OPACITY,
  TEXT_FONT_WEIGHT,
  TEXT_SIZE,
  TEXT_KIND,
  TEXT_ALIGN,
  TEXT_BORDER,
} from "@/bit/components/next13.components.text";
import { LABEL_TYPE, LABEL_FUNC } from "./enums";

export { LABEL_TYPE };

const labelThemes = {
  promo: { color: TEXT_COLOR.PURPLE, background: TEXT_COLOR.PURPLE },
  active: { color: TEXT_COLOR.WHITE, background: TEXT_COLOR.BLUE },
  inactive: { color: TEXT_COLOR.GREY4, background: TEXT_COLOR.GREY4 },
  success: { color: TEXT_COLOR.GREEN, background: TEXT_COLOR.GREEN },
  warning: { color: TEXT_COLOR.ORANGE, background: TEXT_COLOR.ORANGE },
  error: { color: TEXT_COLOR.RED, background: TEXT_COLOR.RED },
  discount: { color: TEXT_COLOR.WHITE, background: TEXT_COLOR.RED_DARK },
  gift: { color: TEXT_COLOR.WHITE, background: TEXT_COLOR.BLUE_DARK },
  spent: { color: TEXT_COLOR.WHITE, background: TEXT_COLOR.GREY4 },
};

/**
 * @name Label
 * @description atomic component
 * @see {@link https://bit.cloud/gerenciatd/next13/components/label}
 * @param {any} props
 * @returns {React.FC}
 */
const Label = ({
  theme,
  inversed,
  layout,
  notBorderRadius,
  label,
  customColors,
  className,
  func,
  oper1,
  oper2,
  custoObjectInfo,
  cyData,
}) => {
  if (!label) return null;

  const {
    color: customColor = customColors?.color,
    background: customBackground = customColors?.background,
  } = customColors;

  const {
    color: themeColor = labelThemes[theme]?.color || TEXT_COLOR.PURPLE,
    background: themeBackground = labelThemes[theme]?.background ||
      TEXT_COLOR.PURPLE,
  } = labelThemes[theme] || {};

  const labelColor = customColor || themeColor;
  const labelBg = inversed
    ? TEXT_COLOR.WHITE
    : customBackground || themeBackground;

  if (func) {
    try {
      if (func === LABEL_FUNC.SAVING_VS_FREE) {
        let result = Math.round(
          parseFloat(oper1.replace(",", ".")) -
            parseFloat(oper2.replace(",", ".")) * 48 -
            1
        );
        label = label.replace("{func}", result);
      }
    } catch {
      label = "";
    }
  }
  return (
    <>
      <TextInfo
        text={label}
        color={labelColor}
        backgroundColor={labelBg}
        kind={TEXT_KIND.SPAN}
        size={TEXT_SIZE.CAPTION2}
        fontType={TEXT_FONT_WEIGHT.REGULAR}
        align={TEXT_ALIGN.CENTER}
        border={notBorderRadius ? TEXT_BORDER.NONE : TEXT_BORDER.ROUNDED}
        bgOpacity={notBorderRadius ? TEXT_OPACITY[0] : TEXT_OPACITY[10]}
        className={`label ${className} label-theme--${theme || "custom"}${
          inversed ? "--inversed" : ""
        } layout--${layout} border-radius--${notBorderRadius} text-content`}
        custoObjectInfo={custoObjectInfo}
        cyData={cyData}
      />
      <style jsx>{`:global(.label) {
  padding: 0.25rem 0.75rem;
  min-width: 3.5rem;
  width: fit-content;
  height: fit-content;
}

:global(.label.layout--fantasy) {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0.1rem 1rem !important;
  border-radius: 1rem 0;
  z-index: 20;
}

:global(.label.layout--full) {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0.1rem 1rem !important;
  border-radius: 0;
  z-index: 20;
  width: 100%;
}
`}</style>
    </>
  );
};

Label.propTypes = {
  /**
   * Define el tema del label y puede ser uno de los siguientes valores: "promo", "active", "inactive", "success", "warning" o "error".
   */
  theme: PropTypes.string,
  /**
   * Define el como se posiciona el label y puede ser uno de los siguientes valores: "default", "full" o "fantasy".
   */
  layout: PropTypes.string,
  /**
   * Texto del label.
   */
  label: PropTypes.string.isRequired,
  /**
   * Si es verdadero el fondo será blanco, pero manteniendo el color del texto del tema.
   */
  inversed: PropTypes.bool,
  /**
   * Si es verdadero los bordes no son redondeados.
   */
  notBorderRadius: PropTypes.bool,
  /**
   * Objeto que se utiliza para añadir colores personalizados al label. Debe tener dos propiedades opcionales:
   * - color: el color del texto personalizado.
   * - background: el color de fondo personalizado.
   */
  customColors: PropTypes.shape({
    color: PropTypes.string,
    background: PropTypes.string,
  }),
  /**
   * Clase para añadirle estilos extras al icono.
   */
  className: PropTypes.string,
  /**
   * Objeto para la customización del icono en dev.
   */
  custoObjectInfo: PropTypes.shape({
    componentId: PropTypes.string,
    componentInstance: PropTypes.string,
    editFunction: PropTypes.func,
  }),
  cyData: PropTypes.string,
};

Label.defaultProps = {
  theme: "",
  layout: "default",
  inversed: false,
  notBorderRadius: false,
  className: "",
  customColors: {},
  custoObjectInfo: null,
  cyData: "",
};

export default Label;
